import React, { useCallback, useMemo } from 'react';
import { isNull } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { AccessGroup, AccessGroupTableName, getAccessGroupColumnSort, getAccessGroupFilters, getFilteredAccessGroupUsers } from './store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { useAppSelector } from '../../../hooks/react-redux';
import { UserRole } from '../../constants/permittedRoles';
import { Toggle } from '../../shared/toggle';
import { AccessGroupTableHeader } from './AccessGroupTableHeader';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { getAllUsers, getIsFetchingUsers } from '../users/store';
import { getAllRoles } from '../roles/store';

interface AccessGroupUsersTableProps {
    roleIds: number[];
    userIds: number[];
    updateGroup: (key: keyof AccessGroup, ids: number[]) => void;
    scrollableHeight: number;
}

export const AccessGroupUsersTable: React.FC<AccessGroupUsersTableProps> = ({ roleIds, userIds, updateGroup, scrollableHeight }) => {
    const allUsers = useAppSelector(getAllUsers);
    const allRoles = useAppSelector(getAllRoles);
    const columnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.USER));
    const filters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.USER));
    const filteredUsers = useAppSelector(getFilteredAccessGroupUsers);
    const isFetchingUsers = useAppSelector(getIsFetchingUsers);

    const toggleUserInAccessGroup = useCallback((userId: number, value: boolean) => {
        const updatedUserIds = !value ? userIds.filter(id => id !== userId) : [...userIds, userId];
        updateGroup('userIds', updatedUserIds);
    }, [userIds, updateGroup]);

    const getUserInfo = useCallback(
        (userId: number, role: UserRole, roleId: number | null) => {
            const user = allUsers.find(user => user.userId === userId)!;
            const isRoleIncluded = !isNull(user.roleId) && roleIds.includes(user.roleId!);
            let userRole = role === UserRole.ADMIN ? 'Admin' : 'User';
            if (!isNull(roleId)) {
                userRole = allRoles.find(eachRole => eachRole.roleId! === roleId)?.roleName || '';
            }
            const inGroup = userIds.includes(userId) || isRoleIncluded;
            const disabled = isRoleIncluded;
            const disabledTooltip = disabled ? ['This user is part of a role toggled on for this access group so cannot be changed'] : [];
            return { inGroup, disabled, disabledTooltip, userRole };
        },
        [allUsers, userIds, roleIds, allRoles]
    );

    const allSelected = useMemo(() => allUsers.map(({ userId }) => userId).every(userId => userIds.includes(userId)) && allUsers.length > 0, [userIds, allUsers]);

    const toggleSelectAll = useCallback(() => {
        const updatedUserIds = allSelected ? [] : allUsers.map(({ userId }) => userId);
        updateGroup('userIds', updatedUserIds);
    }, [allSelected, allUsers, updateGroup]);

    const tableHeaders = [
        { width: '30%', title: 'Name', column: 'user' },
        { width: '30%', title: 'Username', column: 'username' },
        { width: '20%', title: 'Role', column: 'role' },
        { width: '20%', title: 'Included', column: 'userIncluded' }
    ];

    return (
        <div className={styles.accessGroupContentWrapper}>
            <AccessGroupTableHeader
                allSelected={allSelected}
                toggleSelectAll={toggleSelectAll}
                textFilterColumns={['user', 'username']}
                tableHeaders={tableHeaders}
                selectAllLabel='Users'
                filters={filters}
                columnSort={columnSort}
                tableName={AccessGroupTableName.USER}
            />
            <Scrollable height={`${scrollableHeight}px`}>
                {isFetchingUsers ? <div className={styles.tableLoadingDots} style={{ height: scrollableHeight }}><LoadingDots /></div> :
                    filteredUsers.map(({ userId, username, forenames, surname, role, roleId }) => {
                        const name = `${forenames} ${surname}`;
                        const { inGroup, disabled, disabledTooltip, userRole } = getUserInfo(userId, role, roleId);
                        return (
                            <div key={userId} className={styles.tableRow}>
                                <div className={styles.tableCell} style={{ width: '30%' }}>{name}</div>
                                <div className={styles.tableCell} style={{ width: '30%' }}>{username}</div>
                                <div className={styles.tableCell} style={{ width: '20%' }}>{userRole}</div>
                                <div className={styles.tableCell} style={{ width: '20%' }}>
                                    <Toggle
                                        onChange={value => toggleUserInAccessGroup(userId, value)}
                                        checked={inGroup}
                                        disabled={disabled}
                                        disabledTooltip={disabledTooltip}
                                    />
                                </div>
                            </div>
                        );
                    })
                }
            </Scrollable>
        </div>
    );
};
